/**
 * WCC360 — derived roadmap + pathway routes for any job role.
 *
 * Presentation-layer derivation only. Nothing here is scored and nothing is
 * written. Every action traces back to a field on the sealed Career
 * Intelligence v3.2 projection (`src/lib/careerIntel.ts`), so a role with no
 * evidence for a stage simply doesn't get that stage — never a placeholder.
 *
 * Two views come out of it:
 *  · `careerPathwayRoutes` — the study/qualification routes, ordered cheapest
 *    route first (an "A OR B OR C" entry line means the cheapest of the three
 *    is the real requirement).
 *  · `careerRoadmap` — a staged timeline from "this month" to "stretch", built
 *    from subjects → pathways → must-haves → nice-to-haves → adjacent/stretch.
 */
import type { CareerIntel } from "@/lib/careerIntel";
import { careerLevelTier, type LevelTier } from "@/lib/salaryBands";
import { careerSwitcherRoadmap, isCareerSwitcher } from "@/lib/careerSwitcherRoadmap";

/* ------------------------------------------------------------------ */
/* Pathway routes                                                      */
/* ------------------------------------------------------------------ */

export type RouteTier = 1 | 2 | 3 | 4;

export interface PathwayRoute {
  /** Verbatim registry text for the route. */
  label: string;
  /** Human name for the kind of route this is. */
  kind: string;
  /** 1 = shortest/cheapest, 4 = longest. */
  tier: RouteTier;
  /** Rough time on the route. */
  duration: string;
  /** Why someone would take this one. */
  note: string;
}

interface RouteRule {
  kind: string;
  tier: RouteTier;
  duration: string;
  note: string;
  match: RegExp;
}

/** Ordered cheapest-first; the first rule that matches wins. */
const ROUTE_RULES: RouteRule[] = [
  {
    kind: "Short course or certification",
    tier: 1,
    duration: "Weeks to a few months",
    note: "The quickest way in if you can show work alongside it.",
    match: /(certificat|certif|bootcamp|nsqf|micro-?credential|online|swayam|nptel|mooc|short course|licence|license|rto)/i,
  },
  {
    kind: "Apprenticeship or on-the-job",
    tier: 1,
    duration: "1–3 years, earning while you learn",
    note: "You're paid from day one and the experience counts as qualification.",
    match: /(apprentice|naps|nats|iti\b|trainee|on-the-job|internship|site experience)/i,
  },
  {
    kind: "Diploma or polytechnic",
    tier: 2,
    duration: "2–3 years",
    note: "Cheaper and shorter than a degree, and widely accepted for this work.",
    match: /(diploma|polytechnic|associate|btec|foundation)/i,
  },
  {
    kind: "Degree",
    tier: 3,
    duration: "3–4 years",
    note: "The most common route, and the one most employers recognise first.",
    match: /(btech|b\.?tech|be\b|bsc|b\.?sc|bba|bcom|bca|ba\b|bachelor|llb|mbbs|degree|university|college|iit|nit|iiit)/i,
  },
  {
    kind: "Postgraduate or professional",
    tier: 4,
    duration: "1–3 years after a degree",
    note: "Usually taken later, for specialising or moving up — rarely needed to start.",
    match: /(mtech|m\.?tech|msc|m\.?sc|mba|mca|ma\b|master|phd|doctor|pg\b|icsi|ca\b|cfa|cat\/xat|xat|cat\b|pmp)/i,
  },
];

function classify(label: string): RouteRule {
  for (const rule of ROUTE_RULES) if (rule.match.test(label)) return rule;
  return {
    kind: "Route into the work",
    tier: 2,
    duration: "Varies",
    note: "Ask two people already doing this how they arrived — routes here vary.",
    match: /.^/,
  };
}

function tidy(text: string): string {
  return text.replace(/\s+/g, " ").trim().replace(/^[-–·•]\s*/, "");
}

/**
 * Study and qualification routes for a role, cheapest route first.
 * Duplicate kinds collapse to the first (most concrete) example.
 */
export function careerPathwayRoutes(intel: CareerIntel): PathwayRoute[] {
  const raw = [...intel.pathways, ...(intel.entry ? intel.entry.split(/\bor\b|;|\+/i) : [])]
    .map(tidy)
    .filter((s) => s.length > 2 && !/^varies by country/i.test(s));

  const seenLabel = new Set<string>();
  const routes: PathwayRoute[] = [];
  for (const label of raw) {
    const key = label.toLowerCase();
    if (seenLabel.has(key)) continue;
    seenLabel.add(key);
    const rule = classify(label);
    routes.push({
      label: label.charAt(0).toUpperCase() + label.slice(1),
      kind: rule.kind,
      tier: rule.tier,
      duration: rule.duration,
      note: rule.note,
    });
  }

  // Cheapest first, stable within a tier by codepoint so the order is deterministic.
  routes.sort((a, b) => (a.tier - b.tier) || (a.label < b.label ? -1 : a.label > b.label ? 1 : 0));

  const perKind = new Map<string, number>();
  return routes.filter((r) => {
    const n = perKind.get(r.kind) ?? 0;
    if (n >= 3) return false;
    perKind.set(r.kind, n + 1);
    return true;
  });
}

/* ------------------------------------------------------------------ */
/* Roadmap                                                             */
/* ------------------------------------------------------------------ */

export interface RoadmapStage {
  id: string;
  /** When this happens. */
  horizon: string;
  /** What the stage is for. */
  title: string;
  /** One warm sentence framing the stage. */
  summary: string;
  /** Concrete things to do, each traceable to registry evidence. */
  actions: string[];
}

/**
 * Where the learner is standing today. Everything optional: with none of it we
 * fall back to the role-shaped roadmap, which is what every visitor used to
 * get.
 */
export interface LearnerStart {
  /** Highest qualification finished. */
  qualification?: "school" | "diploma" | "bachelors" | "masters" | null;
  /** Years of paid experience. */
  yearsExperience?: number | null;
  band?: string | null;
  primaryGoal?: string | null;
  careerIntent?: "deepen" | "adjacent" | "switch" | "unsure" | null;
}

/** Typical years in the work before a role at this seniority opens up. */
const YEARS_FOR_TIER: Record<LevelTier, number> = {
  entry: 0, mid: 3, senior: 7, executive: 12, unknown: 0,
};

export interface RoleReach {
  tier: LevelTier;
  /** Years usually needed before this title is open. */
  yearsNeeded: number;
  yearsHave: number;
  /** Years between where they are and this title. 0 = open to them now. */
  distanceYears: number;
  /** True when this role is a realistic next step, not a later one. */
  reachableNow: boolean;
}

/**
 * How far this role is from the person reading about it.
 *
 * This exists because handing an entry-level learner a "land the first role"
 * step for a Director title is a lie dressed as encouragement. The role stays
 * on the map — we just show the route to it instead of pretending it's next.
 */
export function roleReach(intel: CareerIntel, start?: LearnerStart | null): RoleReach {
  const tier = careerLevelTier(intel.level);
  const yearsNeeded = YEARS_FOR_TIER[tier];
  const yearsHave = Math.max(0, start?.yearsExperience ?? 0);
  const distanceYears = Math.max(0, yearsNeeded - yearsHave);
  return { tier, yearsNeeded, yearsHave, distanceYears, reachableNow: distanceYears === 0 };
}

const list = (items: string[], max = 3) => items.slice(0, max).join(", ");

/**
 * A staged roadmap for a role. Stages with no evidence are dropped, so a
 * thin registry row yields a shorter — never a fabricated — roadmap.
 *
 * Pass `start` and the roadmap becomes a route rather than a brochure: stages
 * the learner has already cleared are dropped, and a role above their current
 * level is shown as somewhere the route arrives, not as the next application.
 */
export function careerRoadmap(intel: CareerIntel, start?: LearnerStart | null): RoadmapStage[] {
  if (isCareerSwitcher(start)) return careerSwitcherRoadmap(intel);
  const routes = careerPathwayRoutes(intel);
  const cheapest = routes[0];
  const reach = roleReach(intel, start);
  const experienced = reach.yearsHave >= 3;
  const qualified = start?.qualification === "bachelors" || start?.qualification === "masters";
  const stages: RoadmapStage[] = [];

  /* 1 · Explore */
  const exploreActions: string[] = [];
  if (intel.dayInLife)
    exploreActions.push("Read the day-in-the-life above and mark the parts you'd look forward to.");
  if (intel.interests.length)
    exploreActions.push(`Try one small project that touches ${list(intel.interests, 2)}.`);
  if (intel.employers.length)
    exploreActions.push(`Find one person working at ${intel.employers[0]} and ask them how they started.`);
  if (intel.aliases.length)
    exploreActions.push(`Search job adverts for "${intel.name}" and "${intel.aliases[0]}" — read five.`);
  else exploreActions.push(`Search job adverts for "${intel.name}" and read five of them.`);
  stages.push({
    id: "explore",
    horizon: "This month",
    title: experienced ? "Check this is the move you want" : "Find out if you'd like it",
    summary: experienced
      ? "You've worked. Before any course or move, get close enough to this work to be sure."
      : "Before any course or fee, spend a few hours near the actual work.",
    actions: exploreActions,
  });

  /* 2 · Foundations — dropped for someone who already has both the
     qualification and the years. Telling them to pick up school subjects
     would waste the one stage they'd actually read. */
  if (!(experienced && qualified)) {
    const foundation: string[] = [];
    if (intel.subjects.length)
      foundation.push(`Keep or pick up ${list(intel.subjects, 3)} — these carry the most weight here.`);
    if (intel.mustHave.length)
      foundation.push(`Start on one must-have: ${intel.mustHave[0]}.`);
    if (intel.learningStyle)
      foundation.push(`People in this work learn best by ${intel.learningStyle.toLowerCase()} — set it up that way.`);
    foundation.push("Keep a simple log of what you make. It becomes your evidence later.");
    stages.push({
      id: "foundations",
      horizon: "Next 6–12 months",
      title: "Build the foundations",
      summary: "The subjects and habits that make every later route easier.",
      actions: foundation,
    });
  }

  /* 3 · Qualify */
  if (routes.length) {
    const qualify: string[] = [
      `Cheapest workable route: ${cheapest.label} — ${cheapest.duration.toLowerCase()}.`,
    ];
    if (routes[1]) qualify.push(`Alternative: ${routes[1].label}.`);
    if (intel.entry && !/^varies by country/i.test(intel.entry))
      qualify.push(`What's asked for: ${intel.entry}`);
    if (experienced)
      qualify.push(`Your ${reach.yearsHave} year${reach.yearsHave === 1 ? "" : "s"} of work counts here — experience usually substitutes for a tier of qualification, so start by asking what they'll waive.`);
    qualify.push("Compare total cost and time for two routes before you commit to either.");
    stages.push({
      id: "qualify",
      horizon: experienced ? "Alongside the work you already have" : "1–4 years",
      title: experienced ? "Close the qualification gap" : "Get qualified",
      summary: "More than one route lands here. Take the one you can actually afford.",
      actions: qualify,
    });
  }

  /* 4 · The way in — the stage that used to hand a senior title to someone
     with no experience. A role above the learner's current level is reached
     through the rung below it, and we say which rung and roughly how long. */
  const first: string[] = [];
  if (intel.mustHave.length)
    first.push(`Be able to show, not just claim: ${list(intel.mustHave, 3)}.`);
  if (intel.employers.length)
    first.push(`Apply where the work sits: ${list(intel.employers, 2)}.`);
  if (intel.environment) first.push(`Expect to work in a ${intel.environment.toLowerCase()} setting.`);

  if (reach.reachableNow) {
    if (intel.level) first.push(`Titles at this level read as: ${intel.level}.`);
    stages.push({
      id: "first-role",
      horizon: experienced ? "Your next move" : "Your first 2 years in",
      title: experienced ? "Make the move" : "Land the first role",
      summary: "Getting in matters more than getting in at the perfect place.",
      actions: first,
    });
  } else {
    const stepping = intel.adjacent.length ? list(intel.adjacent, 2) : "the entry rung in this family";
    first.unshift(
      `Start at ${stepping} — that's the rung ${intel.name} is usually filled from.`,
    );
    first.push(
      `Search adverts for ${intel.name} and note what the people in them did just before. That's your next job title, not this one.`,
    );
    stages.push({
      id: "way-in",
      horizon: "Your first 1–2 years in",
      title: "Start on the rung below",
      summary: `${intel.name} is a ${reach.tier === "executive" ? "senior leadership" : reach.tier} role — usually around ${reach.yearsNeeded} years in. Nothing about that is fixed; it's just the door people come through.`,
      actions: first,
    });
    stages.push({
      id: "target-role",
      horizon: `About ${reach.distanceYears} year${reach.distanceYears === 1 ? "" : "s"} from where you are now`,
      title: `Step into ${intel.name}`,
      summary: "This is where the route arrives. People move faster than this all the time — it's a marker, not a limit.",
      actions: [
        intel.level ? `The title at that point reads as: ${intel.level}.` : `The title at that point: ${intel.name}.`,
        "By then you'll want two things you can point at: work you led, and someone senior who'd vouch for you.",
        ...(intel.niceToHave.length
          ? [`What separates people at this level: ${list(intel.niceToHave, 3)}.`]
          : []),
      ],
    });
  }

  /* 5 · Grow */
  const grow: string[] = [];
  if (intel.niceToHave.length)
    grow.push(`Add what separates people here: ${list(intel.niceToHave, 3)}.`);
  if (intel.futureSkills.length)
    grow.push(`Deepen the skills that hold their value: ${list(intel.futureSkills, 3)}.`);
  if (intel.humanAdvantage)
    grow.push(`Lean into what AI won't take: ${intel.humanAdvantage}.`);
  if (intel.adjacent.length)
    grow.push(`Sideways moves that stay open: ${list(intel.adjacent, 3)}.`);
  if (grow.length)
    stages.push({
      id: "grow",
      horizon: "2–5 years in",
      title: "Grow past the entry rung",
      summary: "This is where pay and choice open up — and where most people coast.",
      actions: grow,
    });

  /* 6 · Stretch */
  const stretch: string[] = [];
  if (intel.stretch.length) stretch.push(`Aim at: ${list(intel.stretch, 3)}.`);
  if (intel.entrepreneurship && /high|medium/i.test(intel.entrepreneurship))
    stretch.push(`Going independent is realistic here (${intel.entrepreneurship.toLowerCase()} potential).`);
  if (intel.freelance && /high/i.test(intel.freelance))
    stretch.push("Freelance demand is strong, so you can trade security for control.");
  if (intel.demand["2040"])
    stretch.push(`Demand is projected ${String(intel.demand["2040"]).toLowerCase()} by 2040 — plan for that, not for today.`);
  if (stretch.length)
    stages.push({
      id: "stretch",
      horizon: "5–10 years",
      title: "Where this can end up",
      summary: "None of this is fixed. It's the shape the road usually takes.",
      actions: stretch,
    });

  return stages;
}
